import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TeamMemberProfile {
  id: string;
  full_name: string;
  phone_number: string | null;
  profile_picture_url: string | null;
  role_description: string | null;
  tour_completed: boolean;
}

interface UseTeamMemberProfileState {
  profile: TeamMemberProfile | null;
  needsSetup: boolean;
  needsTour: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useTeamMemberProfile(merchantId: string | null): UseTeamMemberProfileState {
  const { user } = useAuth();
  const [profile, setProfile] = useState<TeamMemberProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    if (!user || !merchantId) {
      setLoading(false);
      return;
    }

    try {
      // Use type assertion to work with new table before types are regenerated
      const { data, error } = await (supabase as any)
        .from('team_member_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching team member profile:', error);
      }

      setProfile(data as TeamMemberProfile | null);
    } catch (error) {
      console.error('Error fetching team member profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user, merchantId]);

  // Team members (non-super_admin) need setup if no profile exists
  const needsSetup = !loading && !profile;
  
  // Need tour if profile exists but tour not completed
  const needsTour = !loading && profile !== null && !profile.tour_completed;

  return {
    profile,
    needsSetup,
    needsTour,
    loading,
    refetch: fetchProfile,
  };
};
